import React, { useState, useEffect } from 'react';
import { MapPin, Phone, Mail, User, Building2, Loader2, Linkedin, Facebook, Instagram, MessageCircle, Youtube } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { contactInfo as mockContactInfo } from '../data/mock';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { getText, staticTexts } from '../utils/translations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// X (Twitter) icon component
const XIcon = ({ size = 16, className }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

// Messenger icon component
const MessengerIcon = ({ size = 16, className }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.36 2 2 6.13 2 11.7c0 2.91 1.19 5.44 3.14 7.17.16.13.26.35.27.57l.05 1.78c.04.57.61.94 1.13.71l1.98-.87c.17-.08.36-.1.53-.06.91.25 1.87.38 2.9.38 5.64 0 10-4.13 10-9.7C22 6.13 17.64 2 12 2zm5.89 7.73l-2.91 4.6c-.46.73-1.46.92-2.17.42l-2.32-1.73c-.22-.16-.52-.16-.74 0l-3.13 2.38c-.42.32-.97-.18-.69-.62l2.91-4.6c.46-.73 1.46-.92 2.17-.42l2.32 1.73c.22.16.52.16.74 0l3.13-2.38c.42-.32.97.18.69.62z"/>
  </svg>
);

// TikTok icon component
const TikTokIcon = ({ size = 16, className }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Social icon mapping with colors
const SOCIAL_ICONS = {
  linkedin: { icon: Linkedin, color: '#0A66C2', name: 'LinkedIn' },
  facebook: { icon: Facebook, color: '#1877F2', name: 'Facebook' },
  twitter: { icon: XIcon, color: '#000000', name: 'X' },
  instagram: { icon: Instagram, color: '#E4405F', name: 'Instagram' },
  whatsapp: { icon: MessageCircle, color: '#25D366', name: 'WhatsApp' },
  messenger: { icon: MessengerIcon, color: '#0084FF', name: 'Messenger' },
  youtube: { icon: Youtube, color: '#FF0000', name: 'YouTube' },
  tiktok: { icon: TikTokIcon, color: '#000000', name: 'TikTok' }
};

const ContactPage = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [contactCards, setContactCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(true);
  const [socialLinks, setSocialLinks] = useState({});
  const [socialLinksOrder, setSocialLinksOrder] = useState(999);

  const txt = staticTexts.contact;

  // Fetch contact cards and social links from API
  useEffect(() => {
    const fetchContactCards = async () => {
      try {
        const response = await axios.get(`${API}/contact-cards`);
        if (response.data && response.data.length > 0) {
          setContactCards(response.data);
        }
      } catch (error) {
        console.error('Error fetching contact cards:', error);
      } finally {
        setCardsLoading(false);
      }
    };
    
    const fetchSocialLinks = async () => {
      try {
        const response = await axios.get(`${API}/social-links`);
        if (response.data) {
          setSocialLinks(response.data);
        }
      } catch (error) {
        console.error('Error fetching social links:', error);
      }
    };

    const fetchSocialLinksOrder = async () => {
      try {
        const response = await axios.get(`${API}/social-links-order`);
        if (response.data && response.data.contact_order !== undefined) {
          setSocialLinksOrder(response.data.contact_order);
        }
      } catch (error) {
        console.error('Error fetching social links order:', error);
      }
    };
    
    fetchContactCards();
    fetchSocialLinks();
    fetchSocialLinksOrder();
  }, []);

  // Get active social links
  const activeSocialLinks = Object.entries(socialLinks)
    .filter(([key, value]) => value?.is_active && value?.url)
    .sort((a, b) => (a[1].order || 0) - (b[1].order || 0));

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success(getText(language, txt.form.success.en, txt.form.success.fr, txt.form.success.ar));
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast.error(getText(language, txt.form.error.en, txt.form.error.fr, txt.form.error.ar));
    } finally {
      setLoading(false);
    }
  };

  const getCardIcon = (cardType) => {
    switch(cardType) {
      case 'person':
      case 'secretary':
        return User;
      case 'address':
        return MapPin;
      case 'sales_point':
        return Building2;
      default:
        return User;
    }
  };

  const getCardTitle = (card) => {
    if (language === 'ar' && card.title_ar) return card.title_ar;
    if (language === 'fr' && card.title_fr) return card.title_fr;
    return card.title_en || card.title_fr;
  };

  // Render contact cards from API
  const renderContactCards = () => {
    if (cardsLoading) {
      return (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      );
    }

    if (contactCards.length > 0) {
      return (
        <div className="space-y-4">
          {contactCards.map((card) => {
            const Icon = getCardIcon(card.card_type);
            return (
              <div key={card.id} className="bg-white rounded-2xl p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Icon className="text-slate-400" size={18} />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs uppercase tracking-widest text-slate-400 mb-1">
                      {getCardTitle(card)}
                    </p>
                    {card.name && (
                      <p className="font-medium text-slate-800">{card.name}</p>
                    )}
                    {card.phones && card.phones.length > 0 && (
                      <div className="flex flex-wrap gap-3 mt-2">
                        {card.phones.map((phone, index) => (
                          <a 
                            key={index} 
                            href={`tel:${phone}`} 
                            className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1"
                          >
                            <Phone size={12} /> {phone}
                          </a>
                        ))}
                      </div>
                    )}
                    {card.emails && card.emails.length > 0 && (
                      <div className="flex flex-wrap gap-3 mt-1">
                        {card.emails.map((email, index) => (
                          <a 
                            key={index} 
                            href={`mailto:${email}`} 
                            className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1"
                          >
                            <Mail size={12} /> {email}
                          </a>
                        ))}
                      </div>
                    )}
                    {card.address && (
                      <p className="text-sm text-slate-600 mt-2">{card.address}</p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    // Fallback to mock data
    return (
      <div className="space-y-4">
        <div className="bg-white rounded-2xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
              <User className="text-slate-400" size={18} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-slate-400 mb-1">{t('contact.manager')}</p>
              <p className="font-medium text-slate-800">{mockContactInfo.manager.name}</p>
              <div className="flex flex-wrap gap-3 mt-2">
                {mockContactInfo.manager.phones.map((phone, index) => (
                  <a key={index} href={`tel:${phone}`} className="text-sm text-slate-500 hover:text-slate-800">
                    {phone}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
              <MapPin className="text-slate-400" size={18} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-slate-400 mb-1">{t('contact.address')}</p>
              <p className="text-slate-600">{mockContactInfo.mainAddress}</p>
              <div className="mt-3 space-y-1">
                <a href={`tel:${mockContactInfo.mainPhone}`} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800">
                  <Phone size={14} /> {mockContactInfo.mainPhone}
                </a>
                <a href={`mailto:${mockContactInfo.mainEmail}`} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800">
                  <Mail size={14} /> {mockContactInfo.mainEmail}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Social Links Block Component
  const SocialLinksBlock = () => {
    if (activeSocialLinks.length === 0) return null;
    
    return (
      <div className="bg-white rounded-2xl p-6">
        <h3 className="text-xs uppercase tracking-widest text-slate-400 mb-4">
          {language === 'ar' ? 'تابعونا' : language === 'en' ? 'Follow us' : 'Suivez-nous'}
        </h3>
        <div className="flex flex-wrap gap-3">
          {activeSocialLinks.map(([platform, data]) => {
            const socialData = SOCIAL_ICONS[platform];
            if (!socialData) return null;
            const Icon = socialData.icon;
            return (
              <a
                key={platform}
                href={data.url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform"
                style={{ backgroundColor: socialData.color }}
                title={socialData.name}
              >
                <Icon size={20} />
              </a>
            );
          })}
        </div>
      </div>
    );
  };

  // Render contact cards with social links at configured position
  const renderContactCardsWithSocialLinks = () => {
    if (cardsLoading) {
      return (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      );
    }

    const sortedCards = [...contactCards]
      .filter(card => card.is_active !== false)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    // If no cards and no social links, use fallback
    if (sortedCards.length === 0 && activeSocialLinks.length === 0) {
      return renderContactCards();
    }

    // Build items with social links at configured position
    const items = [];
    let socialLinksInserted = false;

    sortedCards.forEach((card, idx) => {
      const cardOrder = card.order ?? idx;
      // Insert social links before this card if its order is >= socialLinksOrder
      if (!socialLinksInserted && socialLinksOrder <= cardOrder && activeSocialLinks.length > 0) {
        items.push({ type: 'social', key: 'social-links' });
        socialLinksInserted = true;
      }
      items.push({ type: 'card', data: card, key: card.id || `card-${idx}` });
    });

    // If social links not inserted yet, add at end
    if (!socialLinksInserted && activeSocialLinks.length > 0) {
      items.push({ type: 'social', key: 'social-links' });
    }

    return (
      <div className="space-y-4">
        {items.map(item => {
          if (item.type === 'social') {
            return <SocialLinksBlock key={item.key} />;
          }
          
          const card = item.data;
          const Icon = getCardIcon(card.card_type);
          
          return (
            <div key={item.key} className="bg-white rounded-2xl p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Icon className="text-slate-400" size={18} />
                </div>
                <div className="flex-1">
                  <p className="text-xs uppercase tracking-widest text-slate-400 mb-1">
                    {getCardTitle(card)}
                  </p>
                  {card.name && (
                    <p className="font-medium text-slate-800">{card.name}</p>
                  )}
                  {card.phones && card.phones.length > 0 && (
                    <div className="flex flex-wrap gap-3 mt-2">
                      {card.phones.map((phone, index) => (
                        <a 
                          key={index} 
                          href={`tel:${phone}`} 
                          className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1"
                        >
                          <Phone size={12} /> {phone}
                        </a>
                      ))}
                    </div>
                  )}
                  {card.emails && card.emails.length > 0 && (
                    <div className="flex flex-wrap gap-3 mt-1">
                      {card.emails.map((email, index) => (
                        <a 
                          key={index} 
                          href={`mailto:${email}`} 
                          className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1"
                        >
                          <Mail size={12} /> {email}
                        </a>
                      ))}
                    </div>
                  )}
                  {card.address && (
                    <p className="text-sm text-slate-600 mt-2">{card.address}</p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Layout>
      {/* Page Header */}
      <section className="pt-20 pb-16 bg-slate-100">
        <div className="container mx-auto px-6">
          <p className="text-xs uppercase tracking-widest text-slate-400 mb-4">
            {getText(language, txt.getInTouch.en, txt.getInTouch.fr, txt.getInTouch.ar)}
          </p>
          <h1 className="text-5xl md:text-6xl font-light text-slate-800">
            {getText(language, txt.title.en, txt.title.fr, txt.title.ar)}
          </h1>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Cards with Social Links at configured position */}
            <div>
              {renderContactCardsWithSocialLinks()}
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-2xl p-8">
              <h2 className="text-2xl font-light text-slate-800 mb-8">
                {getText(language, txt.form.title.en, txt.form.title.fr, txt.form.title.ar)}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm text-slate-600">
                    {getText(language, txt.form.name.en, txt.form.name.fr, txt.form.name.ar)}
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="h-12 border-slate-200 rounded-lg bg-slate-50"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm text-slate-600">
                    {getText(language, txt.form.email.en, txt.form.email.fr, txt.form.email.ar)}
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="h-12 border-slate-200 rounded-lg bg-slate-50"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="subject" className="text-sm text-slate-600">
                    {getText(language, txt.form.subject.en, txt.form.subject.fr, txt.form.subject.ar)}
                  </Label>
                  <Input
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="h-12 border-slate-200 rounded-lg bg-slate-50"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-sm text-slate-600">
                    {getText(language, txt.form.message.en, txt.form.message.fr, txt.form.message.ar)}
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="border-slate-200 rounded-lg bg-slate-50"
                  />
                </div>

                <Button 
                  type="submit" 
                  disabled={loading}
                  className="w-full bg-slate-800 hover:bg-slate-700 text-white h-12 rounded-full"
                >
                  {loading 
                    ? getText(language, txt.form.sending.en, txt.form.sending.fr, txt.form.sending.ar)
                    : getText(language, txt.form.send.en, txt.form.send.fr, txt.form.send.ar)
                  }
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ContactPage;
